import React, { useState } from "react";
import { Dropdown, Icon, Image, Message, MessageContent } from "semantic-ui-react";
import { useAuth0 } from "@auth0/auth0-react";
import CopyToClipboard from "react-copy-to-clipboard";
import { DateTime } from "luxon";

import { useUserContext } from "../../../contexts/UserContext";
import { useRestClientContext } from "../../../contexts/RestClientContext";
import Modal from "../../Modal/Modal";
import Button from "../Button/Button";

import "./ProfileWidget.scss";
import { TimestampedRecord } from "../../../types/types";

interface PersonalTokenResponse extends TimestampedRecord {
	token?: string;
	expires_at: string;
}

function formatTokenExpiry(expiresAt: string): string {
	const date = DateTime.fromISO(expiresAt);
	const absolute = date.toLocaleString(DateTime.DATETIME_FULL);
	const relative = date.toRelative();

	return `Expires: ${absolute} (${relative})`;
}

const ProfileWidget = () => {
	const { logout } = useAuth0();
	const user = useUserContext();
	const client = useRestClientContext();

	const [creatingToken, setCreatingToken] = useState(false);
	const [tokenModal, setTokenModal] = useState<PersonalTokenResponse | null>(null);
	const [tokenModalError, setTokenModalError] = useState<string | null>(null);

	const onSignOut = () =>
		logout({
			returnTo: window.location.origin,
		});

	const onCreateApiToken = () => {
		setCreatingToken(true);
		setTokenModalError(null);
		client
			.createPersonalServiceAccountToken()
			.then((response: Response) => response.json())
			.then((json: PersonalTokenResponse) => {
				setTokenModal(json);
			})
			.catch((err: Error) => {
				setTokenModalError(err.message ?? "Could not create API token.");
			})
			.finally(() => {
				setCreatingToken(false);
			});
	};

	const closeTokenModal = () => {
		setTokenModal(null);
		setTokenModalError(null);
	};

	return (
		<>
			<div className="profile-widget">
				<div>
					<Image src={user.picture} circular />
				</div>
				<div></div>
				<div>
					<Dropdown text={user.nickname}>
						<Dropdown.Menu>
							{user.is_admin && (
								<Dropdown.Item
									text="Create API token"
									disabled={creatingToken}
									onClick={() => onCreateApiToken()}
								/>
							)}
							<Dropdown.Item text="Sign Out" onClick={() => onSignOut()} />
						</Dropdown.Menu>
					</Dropdown>
				</div>
			</div>
			{tokenModal && (
				<Modal title="Your new API token" onClose={closeTokenModal}>
					<Message warning>
						<MessageContent>Copy this token, you will not be able to view it again.</MessageContent>
					</Message>
					<p>{formatTokenExpiry(tokenModal.expires_at)}</p>
					<label>Token:</label>
					<pre className="token-string">{tokenModal.token}</pre>
					<CopyToClipboard text={tokenModal.token ?? ""}>
						<Button variant="contained">
							<Icon name="copy outline" /> Copy token
						</Button>
					</CopyToClipboard>
				</Modal>
			)}
			{tokenModalError && (
				<Modal title="Could not create token" onClose={closeTokenModal}>
					<Message error>
						<p>{tokenModalError}</p>
					</Message>
					<Button variant="contained" onClick={closeTokenModal}>
						Close
					</Button>
				</Modal>
			)}
		</>
	);
};

export default ProfileWidget;
